/**
 * AcumenContext — Ambient AI Overlay for AcuSightPro
 * 
 * Holds the global session context Acumen-10 always has access to.
 * Pages update the context as they load — Acumen is always pre-loaded
 * with what the user is looking at before they even ask.
 */

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { API_URL } from '../config/api';
import { api } from '../config/api';

const AcumenContext = createContext(null);

export function AcumenProvider({ children }) {
  // Global session context — pages update this as they load
  const [sessionContext, setSessionContext] = useState({});

  // Command palette state
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  // Proactive suggestion state
  const [proactiveSuggestion, setProactiveSuggestion] = useState(null);
  const proactiveTimerRef = useRef(null);

  // Update context — called by pages/components as data loads
  const updateContext = useCallback((patch) => {
    setSessionContext(prev => ({ ...prev, ...patch }));
  }, []);

  // Clear context on page change
  const clearContext = useCallback((keys) => {
    if (keys) {
      setSessionContext(prev => {
        const next = { ...prev };
        keys.forEach(k => delete next[k]);
        return next;
      });
    } else {
      setSessionContext({});
    }
  }, []);

  // Core query function — sends prompt + full session context to backend
  const query_acumen = useCallback(async (prompt, mode = 'assist', extraContext = {}) => {
    setLoading(true);
    setResponse(null);
    try {
      const res = await api.post('/ai/acumen', {
        prompt,
        context: { ...sessionContext, ...extraContext },
        mode,
      });
      const text = res.response || res.result || '';
      setResponse(text);
      setHistory(prev => [...prev.slice(-19), { prompt, response: text, mode, ts: Date.now() }]);
      return text;
    } catch (err) {
      const errMsg = 'Acumen is temporarily unavailable.';
      setResponse(errMsg);
      return errMsg;
    } finally {
      setLoading(false);
    }
  }, [sessionContext]);

  // Open palette with optional pre-filled query
  const openPalette = useCallback((prefill = '') => {
    setQuery(prefill);
    setResponse(null);
    setIsOpen(true);
  }, []);

  const closePalette = useCallback(() => {
    setIsOpen(false);
    setQuery('');
    setResponse(null);
  }, []);

  // Proactive suggestion — called when user focuses a contextual field
  // Fires after 800ms of focus, auto-clears after 8s
  const triggerProactive = useCallback((fieldName, prompt) => {
    clearTimeout(proactiveTimerRef.current);
    setProactiveSuggestion(null);
    proactiveTimerRef.current = setTimeout(async () => {
      const text = await query_acumen(prompt, 'proactive', { active_field: fieldName });
      // Only show one-liner for proactive (first sentence)
      const oneLiner = text.split(/\.\s|\n/)[0].replace(/^#+\s*/, '').trim();
      setProactiveSuggestion({ field: fieldName, text: oneLiner });
      // Auto-dismiss after 8s
      setTimeout(() => setProactiveSuggestion(null), 8000);
    }, 800);
  }, [query_acumen]);

  const dismissProactive = useCallback(() => {
    clearTimeout(proactiveTimerRef.current);
    setProactiveSuggestion(null);
  }, []);

  return (
    <AcumenContext.Provider value={{
      sessionContext,
      updateContext,
      clearContext,
      isOpen,
      openPalette,
      closePalette,
      query,
      setQuery,
      response,
      loading,
      history,
      query_acumen,
      proactiveSuggestion,
      triggerProactive,
      dismissProactive,
    }}>
      {children}
    </AcumenContext.Provider>
  );
}

export function useAcumen() {
  const ctx = useContext(AcumenContext);
  if (!ctx) throw new Error('useAcumen must be used within AcumenProvider');
  return ctx;
}
